import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IAlarmaSeguridadActiva,
  IComunicadorAlarma,
  IEventoGenerico,
  IGeoJSONPoint,
  IListado,
  IPopulate,
  ITratamientoEvento,
  IMaterialServicioTecnico,
  IValoresEventoTecnico,
} from 'modelos/src';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { EventosGenericosHttpService } from '../../../../auxiliares/servicios/http/eventos-genericos.service';
import { TratamientoEventosService } from '../../../../auxiliares/servicios/http/tratamientos-eventos.service';
import { MaterialesServicioTecnicoService } from '../../../../auxiliares/servicios/http/materiales-servicio-tecnico.service';
import { EventosGenericosHelperService } from '../helper';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { MapaFacilData } from '../../../../auxiliares/mapa-facil/interfaces';
import { presentacionEstado } from '../../luminarias/estado/estado-luminaria';
import { ModeloDispositivoSelectorCacheService } from '../../../../componentes/selectores/modelo-dispositivo-selector/modelo-dispositivo-selector-cache.service';
import { ultimaComunicacionAlarma } from '../../../../auxiliares/comunicadores-alarma.util';

/** Tipos de alarma de seguridad activa (norma Su-Biao), por módulo. */
const TIPOS_ALARMA_SEGURIDAD_ACTIVA: Record<string, Record<number, string>> = {
  ADAS: {
    1: 'Riesgo de colisión frontal',
    2: 'Salida de carril',
    3: 'Distancia insuficiente con el vehículo de adelante',
    4: 'Riesgo de atropello',
    5: 'Cambios de carril frecuentes',
    6: 'Exceso respecto de una señal de tránsito',
    7: 'Obstáculo adelante',
    16: 'Señal de tránsito reconocida',
    17: 'Captura de la cámara frontal',
  },
  DSM: {
    1: 'Conductor con fatiga',
    2: 'Uso del teléfono al conducir',
    3: 'Fumar al conducir',
    4: 'Conductor distraído',
    5: 'Conductor ausente o cámara de cabina tapada',
    16: 'Captura de la cámara de cabina',
    17: 'Cambio de conductor',
  },
  BSD: {
    1: 'Vehículo detrás',
    2: 'Vehículo detrás a la izquierda',
    3: 'Vehículo detrás a la derecha',
  },
};

/** Nombre de cada módulo para el operador. */
const MODULOS_SEGURIDAD_ACTIVA: Record<string, string> = {
  ADAS: 'Asistencia al conductor (cámara frontal)',
  DSM: 'Monitoreo del conductor (cámara de cabina)',
  TPMS: 'Presión de neumáticos',
  BSD: 'Punto ciego',
};

@Component({
  selector: 'app-detalles-evento-generico',
  templateUrl: './detalles-evento-generico.component.html',
  styleUrl: './detalles-evento-generico.component.scss',
  standalone: false,
})
export class DetallesEventoGenericoComponent implements OnInit {
  data = inject<{
    evento: IEventoGenerico;
  }>(MAT_DIALOG_DATA);
  dialogRef =
    inject<MatDialogRef<DetallesEventoGenericoComponent>>(MatDialogRef);
  private eventosHttp = inject(EventosGenericosHttpService);
  private tratamientosService = inject(TratamientoEventosService);
  private materialesService = inject(MaterialesServicioTecnicoService);
  private eventosHelper = inject(EventosGenericosHelperService);
  helper = inject(HelperService);
  private modeloCache = inject(ModeloDispositivoSelectorCacheService);

  /** comunicadores[] de la alarma con `.modelo` resuelto por idComunicador. */
  private comunicadoresResueltos: IComunicadorAlarma[] = [];

  public readonly Object = Object;
  public loading = true;
  public cargandoTratamientos = false;
  public evento?: IEventoGenerico;
  public tratamientos: ITratamientoEvento[] = [];
  public materiales: IMaterialServicioTecnico[] = [];
  public mapOptions?: MapaFacilData;
  public hasLocation = false;
  public esTecnico = false;

  public infoGeneralData: { [key: string]: any } = {};
  public infoEntidadData: { [key: string]: any } = {};
  /** Detalle de la alarma de seguridad activa de una dashcam (`valores.alarma`). */
  public infoAlarmaData: { [key: string]: any } = {};
  public totalTratamientos = 0;

  private readonly datePipe = new DatePipe('es-AR');

  private readonly populateCompleto: IPopulate[] = [
    {
      path: 'alarma',
      populate: [
        { path: 'domicilio', select: 'direccion geojson' },
        { path: 'modelo', select: 'marca modelo' },
      ],
    },
    {
      path: 'activo',
      select: 'identificacion categoria funcion vehiculo estadoCuenta',
    },
    {
      path: 'tracker',
      select: 'identificacion nombre tipo asignadoA numeroAbonado',
    },
    { path: 'dashcam', select: 'identificacion nombre uniqueId' },
    {
      path: 'luminaria',
      select:
        'identificacion deveui direccion estado tipoDispositivo tipoEnergizado',
    },
    {
      path: 'gateway',
      select: 'nombre gatewayEui nombreChirpstack',
    },
    { path: 'botonBluetooth', select: 'mac serialNumber' },
    { path: 'sirena', select: 'chipId idExterno' },
    { path: 'cliente', select: 'nombre' },
    {
      path: 'detallesTecnicos.tecnico',
      select: 'datosPersonales usuario',
    },
  ];

  public cerrar(): void {
    this.dialogRef.close();
  }

  public getEntityName(): string {
    const e = this.evento;
    if (!e) return '';
    return (
      e.alarma?.nombre ||
      e.activo?.identificacion ||
      e.tracker?.nombre ||
      e.tracker?.identificacion ||
      e.dashcam?.nombre ||
      e.dashcam?.identificacion ||
      e.luminaria?.identificacion ||
      e.gateway?.nombre ||
      e.botonBluetooth?.mac ||
      e.sirena?.chipId ||
      ''
    );
  }

  public getEstado(): string {
    const e = this.evento;
    if (!e) return '';
    return e.estado || '';
  }

  public getTratamientoUsuario(t: ITratamientoEvento): string {
    return t.usuario?.datosPersonales?.nombre || t.usuario?.usuario || '';
  }

  public getTratamientoEstado(t: ITratamientoEvento): string {
    return (this.esTecnico ? t.estadoTecnico : t.estado) || '';
  }

  private formatFecha(fecha?: string): string {
    if (!fecha) return '';
    return this.datePipe.transform(fecha, 'dd/MM/yyyy HH:mm') || '';
  }

  private buildInfoGeneral(): void {
    const e = this.evento!;
    this.esTecnico = e.filtrador === 'Servicio Técnico';

    const datos: { [key: string]: any } = {
      Tipo: e.tipoEvento || null,
      Estado: e.estado || null,
      Categoría: this.eventosHelper.obtenerCategoria(e),
      Título: e.valores?.titulo || null,
      Mensaje: e.valores?.mensaje || null,
      'Fecha de creación': this.formatFecha(e.fechaCreacion),
      Repeticiones: e.repetido ?? null,
      Prioridad: e.prioridad ?? null,
    };
    const usuarioGenerador = (e.valores as { usuario?: string } | undefined)
      ?.usuario;
    if (usuarioGenerador) {
      datos['Usuario'] = usuarioGenerador;
    }

    if (e.valores?.color) {
      datos['Color'] = e.valores.color;
    }

    if (this.esTecnico) {
      const valoresTecnicos = e.valores as IValoresEventoTecnico | undefined;
      const solicitante = valoresTecnicos?.solicitante;
      if (solicitante) {
        if (solicitante.nombre) datos['Solicitante'] = solicitante.nombre;
        if (solicitante.telefono)
          datos['Teléfono solicitante'] = solicitante.telefono;
        if (solicitante.email) datos['Email solicitante'] = solicitante.email;
      }
      if (valoresTecnicos?.disponibilidad)
        datos['Disponibilidad'] = valoresTecnicos.disponibilidad;
      if (valoresTecnicos?.numeroIncidente)
        datos['N° incidente'] = valoresTecnicos.numeroIncidente.toUpperCase();
      if (valoresTecnicos?.prioridad)
        datos['Prioridad'] = valoresTecnicos.prioridad;
      if (e.posponerHasta)
        datos['Posponer hasta'] = this.formatFecha(e.posponerHasta);
    }

    this.infoGeneralData = datos;
  }

  private buildInfoAlarma(): void {
    const alarma = (this.evento?.valores as { alarma?: IAlarmaSeguridadActiva })
      ?.alarma;
    if (!alarma) {
      this.infoAlarmaData = {};
      return;
    }
    const modulo = alarma.modulo || '';
    const tipo =
      alarma.tipo !== undefined
        ? TIPOS_ALARMA_SEGURIDAD_ACTIVA[modulo]?.[alarma.tipo] ||
          `Definida por el fabricante (${alarma.tipo})`
        : null;
    const datos: { [key: string]: any } = {
      Alarma: tipo,
      Módulo: MODULOS_SEGURIDAD_ACTIVA[modulo] || modulo || null,
    };
    if (alarma.nivel) {
      datos['Nivel'] = alarma.nivel === 2 ? '2 (alarma)' : '1 (advertencia)';
    }
    if (alarma.velocidad !== undefined) {
      datos['Velocidad'] = `${alarma.velocidad} km/h`;
    }
    if (alarma.separacionSegundos) {
      datos['Separación'] =
        `${alarma.separacionSegundos.toLocaleString('es-AR')} s`;
    }
    if (alarma.lado) datos['Lado'] = alarma.lado;
    if (alarma.gradoFatiga)
      datos['Grado de fatiga'] = `${alarma.gradoFatiga} de 10`;
    if (alarma.bandera === 'Inicio') {
      datos['Fin'] = alarma.fechaFin
        ? this.formatFecha(alarma.fechaFin)
        : 'Sin fin informado';
    }
    if (alarma.adjuntosAnunciados) {
      datos['Archivos de evidencia en la cámara'] = alarma.adjuntosAnunciados;
    }
    this.infoAlarmaData = datos;
  }

  private buildInfoEntidad(): void {
    const e = this.evento!;
    const datos: { [key: string]: any } = {};

    if (e.alarma) {
      const a = e.alarma;
      datos['Nombre'] = a.nombre || null;
      if (a.domicilio?.direccion) datos['Domicilio'] = a.domicilio.direccion;
      if (a.numeroAbonado) datos['N° abonado'] = a.numeroAbonado;
      if (this.comunicadoresResueltos.length) {
        datos['Comunicador'] =
          this.comunicadoresResueltos
            .map((c) =>
              `${c.modelo?.marca || ''} ${c.modelo?.modelo || ''}`.trim(),
            )
            .filter(Boolean)
            .join(', ') || null;
      }
      if (a.modelo) {
        datos['Modelo'] =
          `${a.modelo.marca || ''} ${a.modelo.modelo || ''}`.trim() || null;
      }
      if (a.estadoCuenta) datos['Estado cuenta'] = a.estadoCuenta;
      if (a.fechaAlta) datos['Alta'] = this.formatFecha(a.fechaAlta);
      const ultimaComunicacion = ultimaComunicacionAlarma(a);
      if (ultimaComunicacion)
        datos['Últ. comunicación'] = this.formatFecha(ultimaComunicacion);
    }

    if (e.activo) {
      const a = e.activo;
      datos['Identificación'] = a.identificacion || null;
      if (a.categoria) datos['Categoría'] = a.categoria;
      if (a.funcion) datos['Función'] = a.funcion;
      if (a.estadoCuenta) datos['Estado cuenta'] = a.estadoCuenta;
      const v = a.vehiculo;
      if (v) {
        if (v.tipo) datos['Tipo vehículo'] = v.tipo;
        if (v.patente) datos['Patente'] = v.patente;
        if (v.marca) datos['Marca'] = v.marca;
        if (v.modelo) datos['Modelo'] = v.modelo;
        if (v.anio) datos['Año'] = v.anio;
        if (v.estado) datos['Estado'] = v.estado;
        if (v.ignicion !== undefined)
          datos['Ignición'] = v.ignicion ? 'Encendida' : 'Apagada';
      }
    }

    if (e.tracker) {
      const t = e.tracker;
      datos['Identificación'] = t.identificacion || null;
      if (t.nombre) datos['Nombre'] = t.nombre;
      if (t.tipo) datos['Tipo'] = t.tipo;
      if (t.asignadoA) datos['Asignado a'] = t.asignadoA;
      if (t.numeroAbonado) datos['N° abonado'] = t.numeroAbonado;
    }

    // Dashcam sin asignar ('Evento Dashcam'); si está asignada, el evento es
    // del activo y lo resuelve el bloque de arriba.
    if (e.dashcam) {
      const d = e.dashcam;
      datos['Identificación'] = d.identificacion || null;
      if (d.nombre) datos['Nombre'] = d.nombre;
      if (d.uniqueId) datos['ID único'] = d.uniqueId;
    }

    if (e.luminaria) {
      const l = e.luminaria;
      datos['Identificación'] = l.identificacion || null;
      if (l.deveui) datos['DevEUI'] = l.deveui;
      if (l.direccion) datos['Dirección'] = l.direccion;
      if (l.tipoDispositivo) datos['Tipo'] = l.tipoDispositivo;
      if (l.estado) {
        const est = l.estado;
        datos['Estado'] = presentacionEstado(est.codigo).label;
        if (est.motivo) datos['Motivo'] = est.motivo;
        if (est.alarmas?.length) datos['Alarmas'] = est.alarmas.join(', ');
        if (est.fechaCambio)
          datos['Estado desde'] = this.formatFecha(est.fechaCambio);
      }
      if (l.tipoEnergizado) datos['Energizado'] = l.tipoEnergizado;
    }

    if (e.gateway) {
      const g = e.gateway;
      datos['nombre'] = g.nombre || null;
      if (g.gatewayEui) datos['gatewayEui'] = g.gatewayEui;
      if (g.nombreChirpstack) datos['nombreChirpstack'] = g.nombreChirpstack;
    }

    if (e.botonBluetooth) {
      const b = e.botonBluetooth;
      datos['MAC'] = b.mac || null;
      if (b.serialNumber) datos['N° serie'] = b.serialNumber;
    }

    if (e.sirena) {
      const s = e.sirena;
      datos['Chip ID'] = s.chipId || null;
      if (s.idExterno) datos['ID externo'] = s.idExterno;
    }

    if (e.cliente) {
      datos['Cliente'] = e.cliente.nombre || null;
    }

    if (this.esTecnico && e.detallesTecnicos?.tecnico) {
      const tecnico = e.detallesTecnicos.tecnico;
      datos['Técnico asignado'] =
        tecnico.datosPersonales?.nombre || tecnico.usuario || null;
    }

    this.infoEntidadData = datos;
  }

  private getGeojson(): IGeoJSONPoint | undefined {
    const e = this.evento!;
    // Alarmas (regulares y técnicas): ubicación en alarma.domicilio.geojson
    if (e.alarma) {
      const geojson = e.alarma.domicilio?.geojson;
      if (geojson?.type === 'Point') return geojson as IGeoJSONPoint;
    }
    // Trackers y activos
    if (
      e.tipoEvento === 'Evento Tracker' ||
      e.tipoEvento === 'Evento Dashcam' ||
      e.tipoEvento === 'Evento Activo' ||
      e.tipoEvento === 'Evento Vehiculo' ||
      e.tipoEvento === 'Evento Colectivo'
    ) {
      return e.valores?.geojson;
    }
    // Luminaria
    if (e.tipoEvento === 'Evento Luminaria') {
      return e.valores?.geojson;
    }

    // Gateway
    if (e.tipoEvento === 'Evento Gateway') {
      return e.valores?.geojson;
    }
    // Botón BLE
    if (e.tipoEvento === 'Evento BotonBLE') {
      return e.valores?.geojson;
    }
    // Sirena
    if (e.tipoEvento === 'Evento Sirena') {
      return e.valores?.geojson;
    }
    return undefined;
  }

  private buildMapOptions(): void {
    const geojson = this.getGeojson();
    if (!geojson?.coordinates || geojson.coordinates.length < 2) {
      this.hasLocation = false;
      return;
    }
    const lng = geojson.coordinates[0] as number;
    const lat = geojson.coordinates[1] as number;
    const coord: [number, number] = [lng, lat];
    this.mapOptions = {
      markers: [
        {
          coor: coord,
          label: 'puntoSimple',
          text: this.evento?.valores?.titulo,
          centrar: true,
        },
      ],
      center: coord,
      zoom: 15,
      satelliteView: true,
    };
    this.hasLocation = true;
  }

  private async cargarEvento(): Promise<void> {
    const id = this.data.evento._id;
    if (!id) {
      this.evento = this.data.evento;
      return;
    }
    const populate = JSON.stringify(this.populateCompleto);
    const result: IListado<IEventoGenerico> =
      await this.eventosHttp.getFiltered({
        filter: JSON.stringify({ _id: id }),
        populate,
        limit: 1,
        page: 0,
      });
    this.evento = result.datos?.[0] || this.data.evento;
  }

  public async cargarMateriales(): Promise<void> {
    if (!this.esTecnico || !this.evento?._id) return;
    try {
      const result = await this.materialesService.getFiltered({
        filter: JSON.stringify({ idEventoGenerico: this.evento._id }),
        populate: JSON.stringify([
          { path: 'usuario', select: 'datosPersonales usuario' },
        ]),
        sort: '-fechaCreacion',
      });
      this.materiales = result.datos || [];
    } catch (error) {
      console.error('Error al cargar materiales', error);
    }
  }

  public async cargarTratamientos(): Promise<void> {
    if (!this.evento?._id) return;
    this.cargandoTratamientos = true;
    const populate: IPopulate[] = [
      { path: 'usuario', select: 'datosPersonales usuario' },
    ];
    try {
      const result = await this.tratamientosService.getFiltered({
        filter: JSON.stringify({ idsEventos: this.evento._id }),
        populate: JSON.stringify(populate),
        sort: '-fechaCreacion',
        limit: 25,
        page: 0,
      });
      this.tratamientos = result.datos || [];
      this.totalTratamientos = result.totalCount || this.tratamientos.length;
      // Atendido por: usuarios únicos que trataron el evento
      const nombres = [
        ...new Set(
          this.tratamientos
            .map((t) => this.getTratamientoUsuario(t))
            .filter(Boolean),
        ),
      ].join(', ');
      if (nombres) {
        this.infoGeneralData['Atendido por'] = nombres;
      }
    } finally {
      this.cargandoTratamientos = false;
    }
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    await this.cargarEvento();
    this.comunicadoresResueltos = await firstValueFrom(
      this.modeloCache.resolverModelosComunicadores(
        this.evento?.alarma?.comunicadores,
      ),
    );
    this.buildInfoGeneral();
    this.buildInfoAlarma();
    this.buildInfoEntidad();
    this.buildMapOptions();
    await Promise.all([this.cargarTratamientos(), this.cargarMateriales()]);
    this.loading = false;
  }
}
